// apps/web/src/components/SearchBar.js

import React, { useState, useEffect, useCallback } from 'react';
import { tasksService } from '../services/api';
import './SearchBar.css';

export default function SearchBar({ workspaceId, labels, collaborators, onTaskSelect }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    assignee: '',
    labels: '',
  });
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);

  // Debounced search function
  const performSearch = useCallback(async () => {
    if (!workspaceId) return;

    const hasQuery = searchQuery.trim().length > 0;
    const hasFilters = filters.status || filters.assignee || filters.labels;

    // Don't search if no query and no filters
    if (!hasQuery && !hasFilters) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const searchFilters = {
        ...(searchQuery.trim() && { q: searchQuery.trim() }),
        ...(filters.status && { status: filters.status }),
        ...(filters.assignee && { assignee: filters.assignee }),
        ...(filters.labels && { labels: filters.labels }),
      };

      const response = await tasksService.searchTasks(workspaceId, searchFilters);
      setSearchResults(response.data?.tasks || []);
      setShowResults(true);
    } catch (err) {
      console.error('[SearchBar] Search failed:', err);
      setError('Failed to search tasks');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [workspaceId, searchQuery, filters]);

  // Debounce the search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch();
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [performSearch]);

  function handleFilterChange(filterName, value) {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
    }));
  }

  function clearSearch() {
    setSearchQuery('');
    setFilters({ status: '', assignee: '', labels: '' });
    setSearchResults([]);
    setShowResults(false);
    setError(null);
  }

  function handleTaskClick(task) {
    setShowResults(false);
    if (onTaskSelect) {
      onTaskSelect(task);
    }
  }

  // Get label names from IDs for display
  function getLabelNames(labelIds) {
    if (!labelIds || labelIds.length === 0) return [];
    const ids = labelIds.split(',').map(id => id.trim());
    return labels.filter(label => ids.includes(label.id));
  }

  const selectedLabels = getLabelNames(filters.labels);

  return (
    <div className="SearchBarContainer" role="search" aria-label="Task search">
      {/* Search Input */}
      <div className="SearchInputWrapper">
        <span className="SearchIcon" aria-hidden="true">🔍</span>
        <input
          type="text"
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="SearchInput"
          aria-label="Search tasks by title or description"
        />
        {(searchQuery || filters.status || filters.assignee || filters.labels) && (
          <button
            onClick={clearSearch}
            className="SearchClearBtn"
            title="Clear search"
            aria-label="Clear search and filters"
          >
            ×
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="FiltersRow">
        {/* Status Filter */}
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className={`FilterSelect ${filters.status ? 'active' : ''}`}
          aria-label="Filter by status"
        >
          <option value="">All Status</option>
          <option value="todo">To Do</option>
          <option value="doing">Doing</option>
          <option value="done">Done</option>
        </select>

        {/* Assignee Filter */}
        <select
          value={filters.assignee}
          onChange={(e) => handleFilterChange('assignee', e.target.value)}
          className={`FilterSelect ${filters.assignee ? 'active' : ''}`}
          aria-label="Filter by assignee"
        >
          <option value="">All Assignees</option>
          <option value="unassigned">Unassigned</option>
          {collaborators.map(collab => (
            <option key={collab.id} value={collab.id}>
              {collab.full_name || collab.username || collab.email}
            </option>
          ))}
        </select>

        {/* Labels Filter */}
        <select
          value={filters.labels}
          onChange={(e) => handleFilterChange('labels', e.target.value)}
          className={`FilterSelect ${filters.labels ? 'active' : ''}`}
          aria-label="Filter by label"
        >
          <option value="">All Labels</option>
          {labels.map(label => (
            <option key={label.id} value={label.id}>
              {label.name}
            </option>
          ))}
        </select>
      </div>

      {/* Active Filters Display */}
      {(filters.status || filters.assignee || selectedLabels.length > 0) && (
        <div className="ActiveFiltersRow">
          {filters.status && (
            <span className="FilterBadge">
              Status: {filters.status}
              <button
                onClick={() => handleFilterChange('status', '')}
                className="FilterBadgeRemove"
                aria-label="Remove status filter"
              >
                ×
              </button>
            </span>
          )}
          {filters.assignee && (
            <span className="FilterBadge">
              Assignee: {filters.assignee === 'unassigned' ? 'Unassigned' :
                collaborators.find(c => c.id === Number(filters.assignee))?.full_name || 'Unknown'}
              <button
                onClick={() => handleFilterChange('assignee', '')}
                className="FilterBadgeRemove"
                aria-label="Remove assignee filter"
              >
                ×
              </button>
            </span>
          )}
          {selectedLabels.map(label => (
            <span
              key={label.id}
              className="FilterBadge"
              style={{
                backgroundColor: label.color || 'var(--accent-secondary)',
              }}
            >
              {label.name}
              <button
                onClick={() => handleFilterChange('labels', '')}
                className="FilterBadgeRemove"
                aria-label={`Remove ${label.name} filter`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search Results Dropdown */}
      {showResults && (
        <div
          className="SearchResultsDropdown"
          role="region"
          aria-label="Search results"
          aria-live="polite"
        >
          {/* Header */}
          <div className="SearchResultsHeader">
            <span className="SearchResultsTitle">
              {isSearching ? 'Searching...' : `${searchResults.length} results`}
            </span>
            <button
              onClick={() => setShowResults(false)}
              className="SearchResultsCloseBtn"
              aria-label="Close search results"
            >
              ×
            </button>
          </div>

          {/* Results List */}
          {error ? (
            <div className="SearchResultsError">
              {error}
            </div>
          ) : searchResults.length === 0 ? (
            <div className="SearchResultsEmpty">
              No tasks found
            </div>
          ) : (
            <div>
              {searchResults.map((task) => (
                <div
                  key={task.id}
                  onClick={() => handleTaskClick(task)}
                  className="SearchResultItem"
                  role="button"
                  aria-label={`Open task: ${task.title}`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleTaskClick(task);
                    }
                  }}
                >
                  <div className="SearchResultHeader">
                    <span className="SearchResultTitle">
                      {task.title}
                    </span>
                    <span className={`SearchResultStatusBadge ${task.status}`}>
                      {task.status}
                    </span>
                  </div>
                  {task.description && (
                    <div className="SearchResultDescription">
                      {task.description.substring(0, 100)}
                      {task.description.length > 100 ? '...' : ''}
                    </div>
                  )}
                  {task.labels && task.labels.length > 0 && (
                    <div className="SearchResultLabels">
                      {task.labels.map(label => (
                        <span
                          key={label.id}
                          className="SearchResultLabel"
                          style={{
                            backgroundColor: label.color || 'var(--accent-secondary)',
                          }}
                        >
                          {label.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
